import React, {useState} from 'react';
import {InfoPageLayout} from '../../components/info-page-layout/info-page-layout.component';
import {Card} from 'primereact/card';
import {Button} from 'primereact/button';
import {SubmitHandler, useForm, Controller } from 'react-hook-form';
import {SelectButton} from 'primereact/selectbutton';
import {classNames} from 'primereact/utils';
import {Slider} from 'primereact/slider';
import {InputNumber} from 'primereact/inputnumber';
import {CalcEntityT, useCalc} from '../../models/calc/calc-provider';
import {rubFormatter} from '../../utils/currercy';
import {useMessage} from '../../providers/message/message-provider';
import {Skeleton} from 'primereact/skeleton';

type InputsT = CalcEntityT;

const defaultValues: InputsT = {
  operation: 0,
  area: 0,
  consumerType: 0,
  length: 0,
  maximumPower: 0,
  reliabilityCategory: 0,
  voltageLevel: 0,
}

const CONSUMER_TYPE_OPTIONS = [
  { name: 'Физическое лицо', value: 0 },
  { name: 'ИП', value: 1 },
  { name: 'Организация', value: 2 },
];

const AREA_OPTIONS = [
  { name: 'Город', value: 0 },
  { name: 'Область', value: 1 },
];

const VOLTAGE_LEVEL_OPTIONS = [
  { name: '0.4', value: 0 },
  { name: '6', value: 1 },
  { name: '10', value: 2 },
  { name: '15', value: 3 },
  { name: '20', value: 4 },
  { name: '35', value: 5 },
];

const RELIABILITY_CATEGORY_OPTIONS = [
  { name: 'I', value: 0 },
  { name: 'II', value: 1 },
  { name: 'III', value: 2 },
];

const CalcPage = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<InputsT>({ defaultValues });
  const {
    doCalc,
    isLoading,
  } = useCalc();
  const {
    showError,
  } = useMessage();

  const [result, setResult] = useState<number | null>(null);

  const getFormErrorMessage = (name: keyof InputsT) => {
    return errors[name] && <small className="p-error">{errors[name]?.message}</small>
  };

  const onSubmit: SubmitHandler<InputsT> = (data) =>
    doCalc(data)
      .then(setResult)
      .catch((e) => {
        showError({
          message: 'Возникла ошибка. Повторите попытку с другими данными',
        })
      });

  return (
    <InfoPageLayout
      title="Стоимость подключения к электросетям"
    >
      <p>
        Стоимость подключения к электросетям определяется в соответствии с приказом ФАС России от 30.06.2022 № 490/22 на
        основании утвержденных ставок платы за технологическое присоединение с учетом мероприятий технических условий.
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid">
          <div className="col-12 md:col-8">
            <Card
              title="Условия"
            >
              <div className="field">
                <label
                  htmlFor="consumerType"
                  className={classNames({'p-error': errors.consumerType})}
                >
                  Категория потребителя
                </label>
                <Controller
                  name="consumerType"
                  control={control}
                  rules={{required: 'Условие обязательно!'}}
                  render={({field, fieldState}) => (
                    <SelectButton
                      id={field.name} {...field} autoFocus
                      className={classNames({'p-invalid': fieldState.invalid})}
                      options={CONSUMER_TYPE_OPTIONS}
                      optionLabel="name"
                      optionDisabled="constant"
                    />
                  )}/>
                {getFormErrorMessage('consumerType')}
              </div>
              <div className="field">
                <label
                  htmlFor="area"
                  className={classNames({'p-error': errors.area})}
                >
                  Территориальное расположение
                </label>
                <Controller
                  name="area"
                  control={control}
                  rules={{required: 'Условие обязательно!'}}
                  render={({field, fieldState}) => (
                    <SelectButton
                      id={field.name} {...field}
                      className={classNames({'p-invalid': fieldState.invalid})}
                      options={AREA_OPTIONS}
                      optionLabel="name"
                      optionDisabled="constant"
                    />
                  )}/>
                {getFormErrorMessage('area')}
              </div>
            </Card>
            <Card>
              <div
                className="grid"
              >
                <div
                  className="col-12 md:col-6"
                >
                  <div className="field">
                    <label
                      htmlFor="length"
                      className={classNames({'p-error': errors.length})}
                    >
                      Расстояние, м
                    </label>
                    <Controller
                      name="length"
                      control={control}
                      rules={{required: 'Условие обязательно!'}}
                      render={({field, fieldState}) => (
                        <div className="w-full">
                          <InputNumber
                            id={field.name}
                            value={field.value}
                            max={10000}
                            onChange={(e) => field.onChange(e.value)}
                            className={classNames('w-full', {'p-invalid': fieldState.invalid})}
                          />
                          <Slider
                            id={field.name}
                            value={field.value}
                            max={10000}
                            onChange={(e) => field.onChange(e.value)}
                            className={classNames('w-full', {'p-invalid': fieldState.invalid})}
                          />
                        </div>
                      )}/>
                    {getFormErrorMessage('length')}
                  </div>
                </div>
                <div
                  className="col-12 md:col-6"
                >
                  <div className="field">
                    <label
                      htmlFor="maximumPower"
                      className={classNames({'p-error': errors.maximumPower})}
                    >
                      Максимальная мощность устройства, кВт
                    </label>
                    <Controller
                      name="maximumPower"
                      control={control}
                      rules={{required: 'Условие обязательно!'}}
                      render={({field, fieldState}) => (
                        <div className="w-full">
                          <InputNumber
                            id={field.name}
                            value={field.value}
                            max={10000}
                            onChange={(e) => field.onChange(e.value)}
                            className={classNames('w-full', {'p-invalid': fieldState.invalid})}
                          />
                          <Slider
                            id={field.name}
                            value={field.value}
                            max={10000}
                            onChange={(e) => field.onChange(e.value)}
                            className={classNames('w-full', {'p-invalid': fieldState.invalid})}
                          />
                        </div>
                      )}/>
                    {getFormErrorMessage('maximumPower')}
                  </div>
                </div>
                <div
                  className="col-12 md:col-6"
                >
                  <div className="field">
                    <label
                      htmlFor="voltageLevel"
                      className={classNames({'p-error': errors.voltageLevel})}
                    >
                      Уровень напряжения, кВ
                    </label>
                    <Controller
                      name="voltageLevel"
                      control={control}
                      rules={{required: 'Условие обязательно!'}}
                      render={({field, fieldState}) => (
                        <SelectButton
                          id={field.name} {...field}
                          className={classNames({'p-invalid': fieldState.invalid})}
                          options={VOLTAGE_LEVEL_OPTIONS}
                          optionLabel="name"
                          optionDisabled="constant"
                        />
                      )}/>
                    {getFormErrorMessage('voltageLevel')}
                  </div>
                </div>
                <div
                  className="col-12 md:col-6"
                >
                  <div className="field">
                    <label
                      htmlFor="reliabilityCategory"
                      className={classNames({'p-error': errors.reliabilityCategory})}
                    >
                      Категория надежности
                    </label>
                    <Controller
                      name="reliabilityCategory"
                      control={control}
                      rules={{required: 'Условие обязательно!'}}
                      render={({field, fieldState}) => (
                        <SelectButton
                          id={field.name} {...field}
                          className={classNames({'p-invalid': fieldState.invalid})}
                          options={RELIABILITY_CATEGORY_OPTIONS}
                          optionLabel="name"
                          optionDisabled="constant"
                        />
                      )}/>
                    {getFormErrorMessage('reliabilityCategory')}
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div className="col-12 md:col-4">
            <Card
              title="Результат"
              subTitle="Заполните все условия и нажмите рассчитать"
              style={{
                backgroundColor: "var(--blue-100)",
              }}
            >
              {
                isLoading && (
                  <React.Fragment>
                    <Skeleton width="100%" height="2rem" />
                    <Skeleton width="80%" height="3rem" />
                  </React.Fragment>
                )
              }
              {
                result && !isLoading && (
                  <React.Fragment>
                    <span>Ориентировочная стоимость с учетом НДС</span>
                    <h1>{rubFormatter.format(result ?? 0)}</h1>
                  </React.Fragment>
                )
              }
              <Button
                label="Рассчитать"
                type="submit"
              />
            </Card>
          </div>
        </div>
      </form>
    </InfoPageLayout>
  )
}

export default CalcPage;